/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-09-30 00:00:00
 *
 *******************************************************/
//@flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import * as MerchandisingV2Actions from 'src/stores/actions/merchandising-v2-actions';
import { NUMBER_PRODUCT_PER_PAGE } from 'src/constants/merchandising-v2';
import { getExcludedProductList, isTypeAllRule } from 'src/helpers/merchandising-v2-helper';
import { waitFor } from 'src/helpers/time-helper';

type State = {
  firstLoad: boolean,
  skip: number, // Index for pagination
  skipOfSearch: number, // Index for pagination of searching text,
  firstPageOptions: Array<Object>, // Contain the data of first page
  inputValue: string
};

function renderSelectProductWrapper(WrappedComponent: Object) {
  class HOCPlan extends Component<ComposedProps, State> {
    state = {
      firstLoad: true,
      skip: 0, // Index for pagination
      skipOfSearch: 0, // Index for pagination of searching text,
      firstPageOptions: [], // Contain the data of first page
      inputValue: '',
      isRemoveInput: false,
      prevOptionFilter: null
    };

    componentDidMount() {
      this.getProducts('', 0, NUMBER_PRODUCT_PER_PAGE);
      this.setState({ firstLoad: true });
    }

    componentWillReceiveProps(nextProps) {
      const { loading, input } = this.props;
      const { loading: loadingNext, list = [], input: inputNext } = nextProps;
      let { firstLoad } = this.state;

      let state = {};
      if (!_.isEmpty(input.value) && inputNext.value) {
        this.setState({ isRemoveInput: true });
      } else {
        this.setState({ isRemoveInput: true });
      }
      if (loading && !loadingNext && firstLoad) {
        // Save data for first time rendering
        // Append loaded options to the list
        state.firstPageOptions = list;
        state.firstLoad = false;
        this.setState(state);
      }
    }

    componentWillUnmount() {
      // Reset states
      this.setState({
        firstLoad: false,
        skip: 0,
        skipOfSearch: 0,
        firstPageOptions: [],
        inputValue: ''
      });
    }

    render() {
      const { options, placeholderEmpty, i18n } = this.props;

      let { placeholder } = this.props;
      placeholder = _.isEmpty(options)
        ? placeholderEmpty || i18n.translate('AdminHTML.Common.Empty.selectProduct_Placeholder')
        : placeholder;

      return (
        <React.Fragment>
          {/* Wrapped Component: Plan, Plan discount, Plan large*/}
          <WrappedComponent
            {...this.props}
            {...this.state}
            loadOptions={this.loadOptions}
            placeholder={placeholder}
          />
        </React.Fragment>
      );
    }

    // Paginate options
    loadOptions = async (inputValue, prevOptions, additional) => {
      const { loading } = this.props;
      let { firstPageOptions = [], inputValue: inputValuePrev } = this.state;

      if (!_.isEmpty(prevOptions)) {
        await waitFor(1000);
      }

      // Avoid calling multiple time
      if (loading) {
        await waitFor(300);
        return this.loadOptions(inputValue, prevOptions);
      }

      // Filter option list by input text
      if (inputValue && !_.isEmpty(inputValue)) {
        return await this.nextPageWithFilter(inputValue, prevOptions);
      } else {
        if (inputValue !== inputValuePrev) {
          // reset input value
          this.setState({ inputValue });
        }
        // Call API for getting more items
        const currentOptions = _.isEmpty(prevOptions) ? firstPageOptions : prevOptions;
        return await this.nextPage(currentOptions, _.isEmpty(prevOptions));
      }
    };

    // Get next page
    nextPage = async (prevOptions, firstDropSelect) => {
      let { skip } = this.state;

      let data = {
        options: prevOptions,
        hasMore: false
      };

      // Get more data
      if (firstDropSelect) skip = 0;
      else skip += NUMBER_PRODUCT_PER_PAGE;
      data = await this.getMore(skip, '', prevOptions);

      // Update state
      this.setState({ skip });

      return data;
    };

    // Get next page if having search text
    nextPageWithFilter = async (inputValue, prevOptions) => {
      let { skipOfSearch = 0, inputValue: inputValuePrev, isRemoveInput } = this.state;

      let data = {
        options: prevOptions,
        hasMore: false
      };

      // Get more data
      skipOfSearch =
        inputValuePrev !== inputValue || isRemoveInput ? 0 : skipOfSearch + NUMBER_PRODUCT_PER_PAGE;
      data = await this.getMore(skipOfSearch, inputValue, prevOptions);

      // Update state
      this.setState({ skip: 0, skipOfSearch, inputValue });

      return data;
    };

    getMore = async (skip, keyword, prevOptions) => {
      let data = {
        options: prevOptions,
        hasMore: false
      };

      // The index of next pagination is inside the limitation of query
      await this.getProducts(keyword, skip, NUMBER_PRODUCT_PER_PAGE);

      const { list = [], totalProductLoad } = this.props;

      let options = data.options;
      if (totalProductLoad > 0) {
        // Append first page since the returned data is not rendered
        // if (prevOptions.length === NUMBER_PRODUCT_PER_PAGE && !keyword) {
        //   options = _.concat(prevOptions, list);
        // } else {
        //   options = list;
        // }
        options = list;
      }
      this.removeExcludedValues(options);

      // Enable flag having more items
      data.hasMore = totalProductLoad === NUMBER_PRODUCT_PER_PAGE;
      data.options = options;
      return data;
    };

    /**
     * Get products
     * @param {*} q
     * @param {*} skip
     * @param {*} limit
     */
    getProducts = async (q, skip, limit) => {
      const { prevOptionFilter } = this.state;
      const { collectionValue, queriesType, ruleType } = this.props;
      const collectionUniversal = isTypeAllRule(queriesType) && ruleType === 'collection';

      // Improvement: Only call api get product if options filter to call not be call before
      if (prevOptionFilter && prevOptionFilter.q === q && prevOptionFilter.skip === skip) {
        return;
      }
      // Save option call data before
      this.setState({
        prevOptionFilter: {
          q,
          skip
        }
      });
      await this.props.getProducts({
        q: encodeURIComponent(q) || '',
        collectionScope: collectionValue,
        skip,
        limit,
        ...(collectionUniversal ? { feature: 'merchandising-v2-collection-universal' } : {})
      });
    };

    removeExcludedValues = options => {
      const { excludedList = [], merchandisingRuleFormValues, input } = this.props;
      if (_.isEmpty(excludedList)) return;

      const selectedProduct = _.get(merchandisingRuleFormValues, input.name, {});
      excludedList.forEach(item => {
        _.remove(options, option => {
          return item.id === option.id && item.id !== selectedProduct?.id;
        });
      });
    };
  }

  const mapDispatchToProps = (dispatch, ownProps) => ({
    // Get products by calling render preview API
    // getProducts: params => dispatch(productActions.getProducts(params, ownProps.i18n))
    getProducts: params => dispatch(MerchandisingV2Actions.getProducts(params, ownProps.i18n))
  });

  const mapStateToProps = (state, props) => {
    // Get variants display setting
    const variantsDisplaySettings = _.get(state.variantsDisplay, 'variantsDisplay', {});

    // Get form values
    const merchandisingRuleFormValues = getFormValues('MerchandisingV2RuleForm')(state);
    const collectionValue = _.get(merchandisingRuleFormValues, 'collection.value', '');

    // Get products based on merchandising conditions
    const products = _.get(state.merchandisingV2, 'products', []);
    // Get excluded list
    const { strategies = [], products: strategyProducts = {} } = merchandisingRuleFormValues || {};
    const excludedList = getExcludedProductList(strategies, strategyProducts);
    const formValues = getFormValues('MerchandisingV2RuleForm')(state);

    // Convert product list
    const list = _.map(products, product => {
      return {
        label: product.title,
        images: product.images,
        price: product.price,
        product_id: product.product_id && product.product_id !== '' ? product.product_id : 0,
        id: product.id,
        value: product.id,
        variant_ids: _.map(product.variants, variant => variant.id)
      };
    });

    return {
      excludedList,
      variantsDisplaySettings,
      merchandisingRuleFormValues,
      list,
      collectionValue,
      total: _.get(state.merchandisingV2, 'totalProducts', 0),
      loading: _.get(state.merchandisingV2, 'loadingMerchandisingProducts', false),
      totalProductLoad: list.length,
      queriesType: formValues.queriesType,
      ruleType: formValues.ruleType
    };
  };

  return withI18n()(connect(mapStateToProps, mapDispatchToProps)(HOCPlan));
}

export default renderSelectProductWrapper;
